import { CSSProperties, useState, useEffect, useRef } from 'react';
import { Modal, Card, Form } from 'react-bootstrap';
import { X, Search } from 'lucide-react';
import { faDroplet, faCheck, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

interface MedicationItem {
    id: string;
    name: string;
    isDrip?: boolean;
    howToGive?: string;
}

interface Protocol {
    protocolId: string;
    drugs?: string[];
    drips?: string[];
}

interface Section {
    name: string;
    drugs: string[];
}

interface EmergencyProtocolsData {
    emergencyProtocols: Array<{
        section: string;
        protocols: Array<{
            name: string;
            id: string;
        }>;
    }>;
}

interface MedicationSearchProps {
    allMedications: MedicationItem[];
    protocols: Protocol[];
    sections: Section[];
    selectedDrugs: string[];    
    protocolsData: EmergencyProtocolsData;
    onClose: () => void;
    onSelectionChange: (drugIds: string[]) => void;
}

const customBadgeStyle = {
    protocol: {
        backgroundColor: '#FDDAE0',
        color: 'black',
        margin: '0 4px',
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        direction: 'rtl'
    } as CSSProperties,
    section: {
        backgroundColor: '#BDEDE7',
        color: 'black',
        margin: '0 4px',
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        direction: 'rtl'
    } as CSSProperties
};

const MedicationSearch = ({
    allMedications,
    protocols,
    sections,
    selectedDrugs,    
    protocolsData,
    onClose,
    onSelectionChange,
}: MedicationSearchProps) => {
    const { t } = useTranslation(['resus']);
    const [localSelectedDrugs, setLocalSelectedDrugs] = useState<string[]>(selectedDrugs);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSection, setSelectedSection] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);
    const modalContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    const handleClose = () => {
        onClose();
    };

    const handleSave = () => {
        onSelectionChange(localSelectedDrugs);
        onClose();
    };

    const getProtocolsForDrug = (drugId: string): string[] => {
        return protocols
            .filter(protocol =>
                protocol.drugs?.includes(drugId) || protocol.drips?.includes(drugId)
            )
            .map(protocol => protocol.protocolId);
    };

    const getSectionsForDrug = (drugId: string): string[] => {
        return sections
            .filter(section => section.drugs.includes(drugId))
            .map(section => section.name);
    };

    const toggleDrugSelection = (drugId: string) => {
        setLocalSelectedDrugs(prev =>
            prev.includes(drugId)
                ? prev.filter(id => id !== drugId)
                : [...prev, drugId]
        );
    };

    const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSection(e.target.value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const filteredMedications = allMedications.filter(med => {
        const matchesSearch = !searchTerm || 
            med.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesSection = !selectedSection || 
            getSectionsForDrug(med.id).includes(selectedSection);

        return matchesSearch && matchesSection;
    });

    return (
        <Modal
            show={true}
            onHide={handleClose}
            size="lg"
            centered
            dialogClassName="modal-90h"
        >
            <div ref={modalContentRef} style={{ direction: 'rtl', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                <Modal.Header className="position-relative">
                    <button
                        className="btn btn-link p-0 position-absolute"
                        onClick={handleClose}
                        style={{ left: '0.5rem', top: '0.5rem', color: 'black' }}
                    >
                        <X size={20} />
                    </button>
                    
                    <div className="w-100 mt-4">
                        <div className="d-flex flex-column flex-md-row justify-content-center gap-3 align-items-center">
                            <div className="search-wrapper" style={{ maxWidth: '250px', width: '100%' }}>
                                <Form.Select
                                    value={selectedSection}
                                    onChange={handleSectionChange}
                                    className="w-100 select-filter"
                                >
                                    <option value="">{t('medicationSearch.filterBy')}</option>
                                    {sections.map(section => (
                                        <option key={section.name} value={section.name}>
                                            {section.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            
                            <div className="search-wrapper" style={{ maxWidth: '250px', width: '100%' }}>
                                <div className="position-relative">
                                    <Form.Control
                                        ref={searchInputRef}
                                        placeholder={t('medicationSearch.searchPlaceholder')}
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        style={{ textAlign: 'right', paddingLeft: '30px' }}
                                        className="w-100"
                                    />
                                    <Search 
                                        size={16} 
                                        className="position-absolute" 
                                        style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mt-3">
                            <div className="d-flex gap-3">
                                <button 
                                    className="btn p-0 med-search-link" 
                                    onClick={() => setLocalSelectedDrugs([...new Set([...localSelectedDrugs, ...filteredMedications.map(med => med.id)])])}
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                    <span className="me-10">{t('medicationSearch.selectAll')}</span>
                                    
                                </button>
                                <button 
                                    className="btn p-0 med-search-link" 
                                    onClick={() => setLocalSelectedDrugs(prev =>
                                        prev.filter(id => !filteredMedications.find(med => med.id === id))
                                    )}
                                >
                                    <FontAwesomeIcon icon={faArrowsRotate} />
                                    <span className="me-10">{t('medicationSearch.clearAll')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="overflow-auto flex-grow-1 p-3">
                    {filteredMedications.map(med => (
                        <Card 
                            key={med.id} 
                            className="medication-card" 
                            onClick={() => toggleDrugSelection(med.id)}
                        >
                            <Card.Body className="d-flex align-items-center" style={{ direction: 'ltr' }}>
                                <div className="d-flex align-items-center justify-content-center" style={{ minWidth: '32px' }}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`drug-${med.id}`}
                                        checked={localSelectedDrugs.includes(med.id)}
                                        className="custom-checkbox"
                                        onChange={() => {}}
                                    />
                                </div>

                                <div className="flex-grow-1 d-flex justify-content-between align-items-center">
                                    <div className="medication-name">
                                        <span className="h6 mb-0">
                                            {med.isDrip && (
                                                <FontAwesomeIcon
                                                    icon={faDroplet}
                                                    className="me-2"
                                                />
                                            )}
                                            {med.name}
                                        </span>
                                        {!med.isDrip && med.howToGive && (
                                            <span className="text-muted small ms-2">
                                                ({med.howToGive})
                                            </span>
                                        )}
                                    </div>

                                    <div className="medication-badges d-none d-md-flex">
                                        {getProtocolsForDrug(med.id).map(protocol => (
                                            <span
                                                key={protocol}
                                                style={customBadgeStyle.protocol}
                                            >
                                                {protocolsData.emergencyProtocols
                                                    .flatMap(section => section.protocols)
                                                    .find(p => p.id === protocol)?.name || protocol}
                                            </span>
                                        ))}

                                        {getSectionsForDrug(med.id).map(section => (
                                            <span
                                                key={section}
                                                style={customBadgeStyle.section}
                                            >
                                                {section}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}

                    {filteredMedications.length === 0 && (
                        <div className="text-center p-4 text-muted">
                            {t('medicationTable.noDrugsFound')}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-top">
                    <button className="btn btn-update"
                            data-testid="medications-search-update"
                            onClick={handleSave}>
                        <FontAwesomeIcon icon={faCheck}  />
                        <span className="me-10">{t('medicationSearch.addButton')}</span>
                    </button>
                </Modal.Footer>
            </div>
        </Modal>
    );
};

export default MedicationSearch;
